// Story 8.12 (ADO-2021, D-6) — which products the signed-in Customer already has on order.
//
// Thin typed wrapper over `apiFetch` for `GET /api/v1/on-order-items?skus=`. The wire shape is a
// LOCK-STEP COPY of order-management's `OnOrderProductDto` (libs/modules/order-management
// contracts/dtos/on-order.dto.ts). A product that is not on order is absent from the response;
// absence is an answer, but only from a chunk that was read. Like `fetchAvailability`, each chunk
// succeeds or fails on its own and the read never rejects: a failed or drifted chunk reports its
// SKUs in `unreadSkus`, so the caller can keep them unknown rather than read an outage as "not on
// order".

import { API_PREFIX, apiFetch } from './shoplink-api';

/** One open order holding the product (lock-step with `OnOrderEntryDto`). */
export interface OnOrderEntry {
  readonly orderId: string;
  readonly orderReference?: string;
  /** UTC ISO 8601. */
  readonly orderedAt: string;
  readonly quantity: number;
  readonly dispatchedQuantity?: number;
  /** `YYYY-MM-DD`. */
  readonly estimatedDeliveryDate?: string;
  readonly depotCode: string;
  readonly depotId?: string;
  readonly depotName?: string;
}

/** One product on order (lock-step with `OnOrderProductDto`). Orders are newest first. */
export interface OnOrderProduct {
  readonly skuCode: string;
  readonly orders: readonly OnOrderEntry[];
}

export const ON_ORDER_PATH = `${API_PREFIX}/on-order-items`;

/** The server's Zod cap on `skus` (`on-order-query.validation.ts`, lock-step). */
export const MAX_ON_ORDER_BATCH_SIZE = 100;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isOptional(value: unknown, type: 'string' | 'number'): boolean {
  return value === undefined || typeof value === type;
}

function isOnOrderEntry(value: unknown): value is OnOrderEntry {
  return (
    isRecord(value) &&
    typeof value['orderId'] === 'string' &&
    typeof value['orderedAt'] === 'string' &&
    typeof value['quantity'] === 'number' &&
    typeof value['depotCode'] === 'string' &&
    isOptional(value['orderReference'], 'string') &&
    isOptional(value['dispatchedQuantity'], 'number') &&
    isOptional(value['estimatedDeliveryDate'], 'string') &&
    isOptional(value['depotId'], 'string') &&
    isOptional(value['depotName'], 'string')
  );
}

/** One batch read. A SKU absent from `products` is NOT on order only when it is not unread. */
export interface OnOrderReadResult {
  /** Products on order, from every chunk that was read. */
  readonly products: readonly OnOrderProduct[];
  /** SKUs whose chunk failed or came back in a shape the guard refused: unknown. */
  readonly unreadSkus: readonly string[];
}

function isOnOrderProduct(value: unknown): value is OnOrderProduct {
  return (
    isRecord(value) &&
    typeof value['skuCode'] === 'string' &&
    Array.isArray(value['orders']) &&
    value['orders'].every(isOnOrderEntry)
  );
}

/**
 * The products among `skuCodes` that are on order, chunked to the server cap. Never rejects: a
 * chunk that fails (non-2xx, network, abort) or returns a body that does not match the contract
 * puts its SKUs in `unreadSkus`, and the other chunks' answers still count.
 */
export async function getOnOrder(
  skuCodes: readonly string[],
  options: { readonly signal?: AbortSignal } = {},
): Promise<OnOrderReadResult> {
  const unique = [...new Set(skuCodes)].filter((sku) => sku !== '');
  if (unique.length === 0) return { products: [], unreadSkus: [] };

  const chunks: string[][] = [];
  for (let start = 0; start < unique.length; start += MAX_ON_ORDER_BATCH_SIZE) {
    chunks.push(unique.slice(start, start + MAX_ON_ORDER_BATCH_SIZE));
  }

  const results = await Promise.all(
    chunks.map(async (chunk): Promise<OnOrderReadResult> => {
      try {
        const res = await apiFetch(
          `${ON_ORDER_PATH}?skus=${encodeURIComponent(chunk.join(','))}`,
          options.signal === undefined ? {} : { signal: options.signal },
        );
        if (!res.ok) throw new Error(`Failed to load on-order items (${res.status})`);
        const body: unknown = await res.json();
        if (!Array.isArray(body) || !body.every(isOnOrderProduct)) {
          throw new Error('Unexpected on-order response shape');
        }
        return { products: body, unreadSkus: [] };
      } catch {
        // This chunk established nothing: its SKUs are unknown, not "not on order".
        return { products: [], unreadSkus: chunk };
      }
    }),
  );
  return {
    products: results.flatMap((result) => result.products),
    unreadSkus: results.flatMap((result) => result.unreadSkus),
  };
}
